import { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Check, Loader2, Car, AlertCircle } from 'lucide-react'
import { usePlanUsage } from '@/hooks/usePlanUsage'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { useAuth } from '@/contexts/AuthContext'
import { useSubscription } from '@/hooks/useSubscription'
import { useTipoOperacao } from '@/hooks/useTipoOperacao'
import { supabase } from '@/integrations/supabase/client'
import { getPublicErrorMessage, getPublicSupabaseFunctionErrorMessage, getSupabaseFunctionErrorMessage } from '@/lib/errors'
import { toast } from 'sonner'

import type { VerticalConfig } from '@/config/verticais'

const buildPlanos = (vertical: VerticalConfig) => [
  {
    id: 'basico' as const,
    nome: 'Básico',
    preco: 149,
    descricao: vertical.planos.descricaoBasico,
    recursos: [
      `20 ${vertical.labels.unidadeMensal} por mês`,
      'Gestão de veículos',
      vertical.documentos.formatoArquivo,
      'Consulta de placa',
      'Histórico FIPE',
      'Suporte por WhatsApp',
    ],
  },
  {
    id: 'pro' as const,
    nome: 'Pro',
    preco: 299,
    descricao: vertical.planos.descricaoPro,
    recursos: [
      `60 ${vertical.labels.unidadeMensal} por mês`,
      'Múltiplos avaliadores',
      'Relatórios avançados',
      `${vertical.documentos.tituloPagina} completo`,
      'Prioridade no suporte',
      'Suporte por WhatsApp',
    ],
    destaque: true,
  },
]

export default function Planos() {
  const [loadingPlano, setLoadingPlano] = useState<string | null>(null)
  const [loadingPortal, setLoadingPortal] = useState(false)
  const [loadingMudanca, setLoadingMudanca] = useState<string | null>(null)
  const [syncing, setSyncing] = useState(false)
  const { user, loja, perfil, reload } = useAuth()
  const { plano, isTrial, isCanceling, trialAvaliacoesRestantes, hasActiveStripeSubscription, status } = useSubscription()
  const { avaliacoesUsadas, limite, limiteAtingido } = usePlanUsage()
  const vertical = useTipoOperacao()
  const planos = buildPlanos(vertical)
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const sucesso = searchParams.get('sucesso') === 'true'
  const portalReturn = searchParams.get('portal') === 'true'
  const isAdmin = perfil?.papel === 'admin'
  const billingBlockedForViewer = Boolean(user) && !isAdmin
  const planoAtualNome = planos.find((item) => item.id === plano)?.nome ?? 'Básico'
  const billingUnavailableMessage = 'Não foi possível atualizar sua assinatura agora. Tente novamente em alguns minutos.'

  // Recarrega dados da loja ao entrar na página
  useEffect(() => {
    if (user) reload()
  }, [reload, user])

  // Ao retornar do portal Stripe, sincroniza o status diretamente com a API do Stripe
  useEffect(() => {
    if (!portalReturn || !user) return
    console.log('[Planos] retornou do portal, iniciando sync-subscription')
    navigate('/planos', { replace: true })
    syncSubscription()
  }, [portalReturn, user, navigate])

  async function syncSubscription() {
    setSyncing(true)
    try {
      const { data, error } = await supabase.functions.invoke('sync-subscription')
      console.log('[Planos] sync-subscription data:', JSON.stringify(data))
      console.log('[Planos] sync-subscription error:', JSON.stringify(error))
      if (error) {
        let msg = error.message ?? 'Erro desconhecido'
        try {
          const body = await (error as any).context?.json?.()
          if (body?.error) msg = body.error
        } catch { /* ignora */ }
        console.error('[Planos] sync-subscription failed:', msg)
        toast.error(billingUnavailableMessage)
      } else {
        toast.success(`Status sincronizado: ${data?.status ?? '—'}`)
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err)
      console.error('[Planos] sync-subscription exception:', msg)
      toast.error(billingUnavailableMessage)
    } finally {
      await reload()
      setSyncing(false)
    }
  }

  useEffect(() => {
    if (sucesso) {
      toast.success('Assinatura ativada com sucesso! Aproveite o WeAuto.')
      navigate('/planos', { replace: true })
    }
  }, [sucesso, navigate])

  async function assinar(planoId: 'basico' | 'pro') {
    if (!user || !loja) {
      navigate('/login')
      return
    }
    if (!isAdmin) {
      toast.error('Apenas admins podem contratar ou alterar planos.')
      return
    }

    setLoadingPlano(planoId)
    try {
      const { data, error } = await supabase.functions.invoke('create-checkout-session', {
        body: { plano: planoId, loja_id: loja.id },
      })

      if (error) {
        const message = await getSupabaseFunctionErrorMessage(error, error.message)
        console.error('Erro da função:', message)
        throw new Error(message)
      }

      if (!data?.url) throw new Error('URL não retornada pela função')
      window.location.href = data.url
    } catch (err: unknown) {
      console.error('Erro ao iniciar checkout:', err)
      toast.error(getPublicErrorMessage(err instanceof Error ? err.message : null, 'Não foi possível abrir o checkout agora. Tente novamente em alguns minutos.'))
    } finally {
      setLoadingPlano(null)
    }
  }

  async function mudarPlano(novoPlano: 'basico' | 'pro') {
    if (!loja) return
    if (!isAdmin) {
      toast.error('Apenas admins podem alterar planos.')
      return
    }
    setLoadingMudanca(novoPlano)
    try {
      const { error } = await supabase.functions.invoke('update-subscription', {
        body: { plano: novoPlano, loja_id: loja.id },
      })
      if (error) {
        const message = await getPublicSupabaseFunctionErrorMessage(error, billingUnavailableMessage)
        console.error('Erro ao mudar plano:', await getSupabaseFunctionErrorMessage(error, error.message))
        throw new Error(message)
      }
      await reload()
      toast.success(`Plano alterado para ${novoPlano === 'pro' ? 'Pro' : 'Básico'} com sucesso!`)
    } catch (err: unknown) {
      console.error('Erro ao mudar plano:', err)
      toast.error(getPublicErrorMessage(err instanceof Error ? err.message : null, billingUnavailableMessage))
    } finally {
      setLoadingMudanca(null)
    }
  }

  async function gerenciarAssinatura() {
    if (!loja) return
    if (!isAdmin) {
      toast.error('Apenas admins podem gerenciar a assinatura.')
      return
    }
    if (!loja.stripe_customer_id) {
      toast.error('Esta loja ainda não possui cliente Stripe para gerenciar.')
      return
    }
    setLoadingPortal(true)
    try {
      const { data, error } = await supabase.functions.invoke('create-portal-session', {
        body: { loja_id: loja.id },
      })

      if (error) {
        const message = await getPublicSupabaseFunctionErrorMessage(error, 'Não foi possível abrir o gerenciamento da assinatura agora.')
        console.error('Erro ao abrir portal:', await getSupabaseFunctionErrorMessage(error, error.message))
        throw new Error(message)
      }
      if (!data?.url) throw new Error('Erro ao abrir portal')
      window.location.href = data.url
    } catch (err: unknown) {
      console.error('Erro ao abrir portal:', err)
      toast.error(getPublicErrorMessage(err instanceof Error ? err.message : null, 'Não foi possível abrir o gerenciamento da assinatura agora.'))
    } finally {
      setLoadingPortal(false)
    }
  }

  return (
    <div className="bg-background">
      <div className="mx-auto max-w-5xl px-4 py-5 sm:px-6 sm:py-6">
        <div className="mb-6 flex items-center gap-3 rounded-2xl border bg-card px-4 py-3 shadow-sm">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary">
            <Car className="h-4 w-4 text-primary-foreground" />
          </div>
          <span className="font-semibold text-lg">WeAuto</span>
          {user && (
            <Button variant="ghost" size="sm" className="ml-auto" onClick={() => navigate('/')}>
              Voltar ao app
            </Button>
          )}
        </div>

        {/* Syncing banner */}
        {syncing && (
          <div className="mb-5 flex items-center gap-3 rounded-lg border bg-muted px-4 py-3 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin shrink-0" />
            <span>Sincronizando status da assinatura...</span>
          </div>
        )}
        {/* Trial banner */}
        {isTrial && (
          <div className="mb-5 flex items-center gap-3 rounded-lg border border-yellow-200 bg-yellow-50 px-4 py-3 text-sm text-yellow-800 dark:border-yellow-800 dark:bg-yellow-950 dark:text-yellow-200">
            <AlertCircle className="h-4 w-4 shrink-0" />
            <span>
              Você está no período gratuito. Restam{' '}
              <strong>{trialAvaliacoesRestantes} {trialAvaliacoesRestantes !== 1 ? 'avaliações' : 'avaliação'}</strong> do seu trial.
            </span>
          </div>
        )}

        {/* Heading */}
        <div className="mb-7 text-center">
          <h1 className="text-3xl font-bold tracking-tight sm:text-4xl">
            {hasActiveStripeSubscription ? 'Sua assinatura' : 'Escolha seu plano'}
          </h1>
          <p className="mt-2 text-base text-muted-foreground sm:text-lg">
            {hasActiveStripeSubscription
              ? 'Gerencie ou altere seu plano a qualquer momento.'
              : isTrial
              ? 'Assine agora e continue usando sem interrupções. Cancele quando quiser.'
              : '5 avaliações grátis para experimentar. Cancele quando quiser.'}
          </p>
          {user && !isAdmin && (
            <p className="mt-3 text-sm text-muted-foreground">
              Somente administradores da loja podem contratar, alterar ou gerenciar a assinatura.
            </p>
          )}
          {loja && hasActiveStripeSubscription && (
            <div className="mt-4 inline-flex items-center gap-2 rounded-full border bg-card px-4 py-2 text-sm">
              <span className="text-muted-foreground">Plano atual:</span>
              <Badge variant="secondary">{planoAtualNome}</Badge>
            </div>
          )}
          {loja && isCanceling && (
            <div className="mt-4 inline-flex items-center gap-2 rounded-full border border-yellow-300 bg-yellow-50 px-4 py-2 text-sm text-yellow-800 dark:border-yellow-700 dark:bg-yellow-950 dark:text-yellow-200">
              <AlertCircle className="h-3.5 w-3.5 shrink-0" />
              <span>Cancelamento agendado — você ainda tem acesso até o fim do período</span>
            </div>
          )}
          {loja && status === 'canceled' && (
            <div className="mt-4 inline-flex items-center gap-2 rounded-full border border-destructive/40 bg-destructive/5 px-4 py-2 text-sm text-destructive">
              <AlertCircle className="h-3.5 w-3.5 shrink-0" />
              <span>Assinatura cancelada — assine novamente para continuar</span>
            </div>
          )}
        </div>

        {/* Uso do mês atual — apenas para planos pagos */}
        {user && hasActiveStripeSubscription && (
          <div className="mx-auto mb-6 max-w-3xl rounded-xl border bg-card px-5 py-4 shadow-sm">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium">Avaliações este mês</span>
              <span className={`text-sm font-semibold ${limiteAtingido ? 'text-destructive' : 'text-foreground'}`}>
                {avaliacoesUsadas} / {limite}
              </span>
            </div>
            <div className="h-2 w-full rounded-full bg-muted overflow-hidden">
              <div
                className={`h-full rounded-full transition-all ${limiteAtingido ? 'bg-destructive' : 'bg-primary'}`}
                style={{ width: `${Math.min((avaliacoesUsadas / limite) * 100, 100)}%` }}
              />
            </div>
            {limiteAtingido && (
              <p className="mt-2 text-xs text-destructive">
                Limite atingido. Faça upgrade para continuar avaliando.
              </p>
            )}
          </div>
        )}

        {/* Pricing cards */}
        <div className="mx-auto grid max-w-3xl gap-5 md:grid-cols-2">
          {planos.map((p) => {
            const isPlanoAtual = hasActiveStripeSubscription && plano === p.id
            const isLoading = loadingPlano === p.id

            return (
              <Card
                key={p.id}
                className={
                  isPlanoAtual
                    ? 'relative border-primary shadow-lg ring-1 ring-primary/20'
                    : p.destaque
                    ? 'border-primary shadow-lg relative'
                    : 'relative'
                }
              >
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 flex gap-2">
                  {p.destaque && (
                    <Badge
                      className="border-primary bg-primary px-3 py-1 text-[10px] font-semibold tracking-[0.16em] text-primary-foreground shadow-[0_8px_24px_hsl(215_25%_20%_/_0.12)]"
                    >
                      Mais popular
                    </Badge>
                  )}
                </div>

                <CardHeader className="pb-3">
                  <CardTitle className="text-xl">{p.nome}</CardTitle>
                  <CardDescription>{p.descricao}</CardDescription>
                  <div className="mt-1.5">
                    <span className="text-3xl font-bold sm:text-4xl">R${p.preco}</span>
                    <span className="text-muted-foreground">/mês</span>
                  </div>
                </CardHeader>

                <CardContent className="pb-5">
                  <ul className="space-y-1.5">
                    {p.recursos.map((r) => (
                      <li key={r} className="flex items-center gap-2 text-sm">
                        <Check className="h-4 w-4 text-primary shrink-0" />
                        {r}
                      </li>
                    ))}
                  </ul>
                </CardContent>

                <CardFooter className="flex flex-col gap-2 pt-0">
                  {isPlanoAtual ? (
                    <>
                      <Badge variant="secondary" className="w-full justify-center py-1.5">
                        Plano atual
                      </Badge>
                      <Button
                        variant="outline"
                        className="w-full"
                        onClick={gerenciarAssinatura}
                        disabled={loadingPortal || billingBlockedForViewer}
                      >
                        {loadingPortal ? (
                          <Loader2 className="h-4 w-4 animate-spin mr-2" />
                        ) : null}
                        Gerenciar assinatura
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button
                        className="w-full"
                        variant={p.destaque ? 'default' : 'outline'}
                        onClick={() => hasActiveStripeSubscription ? mudarPlano(p.id) : assinar(p.id)}
                        disabled={isLoading || !!loadingMudanca || loadingPortal || billingBlockedForViewer || isPlanoAtual}
                      >
                        {(isLoading || loadingMudanca === p.id) ? (
                          <Loader2 className="h-4 w-4 animate-spin mr-2" />
                        ) : null}
                        {isPlanoAtual
                          ? 'Plano já selecionado'
                          : hasActiveStripeSubscription
                          ? 'Mudar para este plano'
                          : (status === 'canceled' || isCanceling) && plano === p.id
                          ? 'Reativar plano'
                          : isTrial
                          ? 'Assinar'
                          : 'Assinar agora'}
                      </Button>
                    </>
                  )}
                </CardFooter>
              </Card>
            )
          })}
        </div>

        <div className="mx-auto mt-5 max-w-3xl rounded-2xl border bg-card px-4 py-3 text-center text-sm text-muted-foreground shadow-sm">
          Pagamento seguro via Stripe.{" "}
          {hasActiveStripeSubscription && isAdmin ? (
            <button
              type="button"
              onClick={gerenciarAssinatura}
              disabled={loadingPortal}
              className="font-medium text-foreground underline underline-offset-4 disabled:opacity-60"
            >
              Cancele
            </button>
          ) : (
            <span className="font-medium text-foreground">Cancele</span>
          )}{" "}
          a qualquer momento sem multa.
        </div>
      </div>
    </div>
  )
}
